import React from 'react';

// ── BOUTONS ──
export function BtnPrimary({ children, onClick, type = 'button', disabled, loading, style }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} style={{
      background: disabled ? 'var(--gray-200)' : 'var(--navy)',
      color: disabled ? 'var(--gray-400)' : 'white',
      border: 'none',
      borderRadius: 'var(--radius-md)',
      padding: '12px 24px',
      fontFamily: 'var(--font-body)',
      fontSize: '0.95rem',
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all var(--transition)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      ...style
    }}>
      {loading && <Spinner size={16} color="white" />}
      {children}
    </button>
  );
}

export function BtnGold({ children, onClick, type = 'button', disabled, style }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      background: 'var(--gold)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--radius-md)',
      padding: '12px 24px',
      fontFamily: 'var(--font-body)',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all var(--transition)',
      ...style
    }}>
      {children}
    </button>
  );
}

export function BtnOutline({ children, onClick, type = 'button', danger, style }) {
  return (
    <button type={type} onClick={onClick} style={{
      background: 'transparent',
      color: danger ? 'var(--error)' : 'var(--navy)',
      border: `1.5px solid ${danger ? 'var(--error)' : 'var(--navy)'}`,
      borderRadius: 'var(--radius-md)',
      padding: '10px 20px',
      fontFamily: 'var(--font-body)',
      fontSize: '0.9rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all var(--transition)',
      ...style
    }}>
      {children}
    </button>
  );
}

// ── SPINNER ──
export function Spinner({ size = 24, color = 'var(--navy)' }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid ${color}22`,
      borderTop: `2px solid ${color}`,
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      flexShrink: 0
    }} />
  );
}

// ── INPUT ──
export function Input({ label, type = 'text', value, onChange, placeholder, error, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--gray-600)' }}>
          {label}{required && <span style={{ color: 'var(--gold-dim)' }}> *</span>}
        </label>
      )}
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{
          padding: '11px 14px',
          border: `1.5px solid ${error ? 'var(--error)' : 'var(--gray-200)'}`,
          borderRadius: 'var(--radius-md)',
          fontSize: '0.95rem',
          background: 'white',
          color: 'var(--gray-800)',
          outline: 'none',
          transition: 'border-color var(--transition)',
          width: '100%'
        }}
        onFocus={e => e.target.style.borderColor = 'var(--navy)'}
        onBlur={e => e.target.style.borderColor = error ? 'var(--error)' : 'var(--gray-200)'}
      />
      {error && <p style={{ fontSize: '0.8rem', color: 'var(--error)', marginTop: 2 }}>{error}</p>}
    </div>
  );
}

// ── TEXTAREA ──
export function Textarea({ label, value, onChange, placeholder, rows = 4, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--gray-600)' }}>{label}</label>}
      <textarea
        value={value} onChange={onChange} placeholder={placeholder} rows={rows}
        style={{
          padding: '11px 14px',
          border: `1.5px solid ${error ? 'var(--error)' : 'var(--gray-200)'}`,
          borderRadius: 'var(--radius-md)',
          fontSize: '0.95rem',
          background: 'white',
          color: 'var(--gray-800)',
          outline: 'none',
          resize: 'vertical',
          transition: 'border-color var(--transition)',
          width: '100%',
          lineHeight: 1.6
        }}
        onFocus={e => e.target.style.borderColor = 'var(--navy)'}
        onBlur={e => e.target.style.borderColor = error ? 'var(--error)' : 'var(--gray-200)'}
      />
      {error && <p style={{ fontSize: '0.8rem', color: 'var(--error)', marginTop: 2 }}>{error}</p>}
    </div>
  );
}

// ── ALERT ──
export function Alert({ type = 'error', children }) {
  const colors = {
    error:   { bg: 'var(--error-bg)',   border: 'var(--error)',   text: 'var(--error)' },
    success: { bg: 'var(--success-bg)', border: 'var(--success)', text: 'var(--success)' },
    warning: { bg: 'var(--warning-bg)', border: 'var(--warning)', text: 'var(--warning)' },
    info:    { bg: 'var(--light-bl)',   border: 'var(--navy)',     text: 'var(--navy)' },
  };
  const c = colors[type];
  return (
    <div style={{
      background: c.bg, borderLeft: `3px solid ${c.border}`,
      borderRadius: 'var(--radius-md)', padding: '12px 16px',
      fontSize: '0.9rem', color: c.text, lineHeight: 1.5
    }}>
      {children}
    </div>
  );
}

// ── BADGE ──
export function Badge({ children, color = 'navy' }) {
  const colors = {
    navy:    { bg: 'var(--light-bl)',   text: 'var(--navy)' },
    gold:    { bg: 'var(--gold-light)', text: 'var(--gold-dim)' },
    green:   { bg: 'var(--success-bg)', text: 'var(--success)' },
    red:     { bg: 'var(--error-bg)',   text: 'var(--error)' },
    gray:    { bg: 'var(--gray-100)',   text: 'var(--gray-600)' },
  };
  const c = colors[color] || colors.navy;
  return (
    <span style={{
      background: c.bg, color: c.text,
      fontSize: '0.75rem', fontWeight: 600,
      padding: '3px 10px', borderRadius: 20,
      letterSpacing: '0.3px'
    }}>
      {children}
    </span>
  );
}

// ── CARD ──
export function Card({ children, style }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--gray-200)',
      padding: '24px',
      ...style
    }}>
      {children}
    </div>
  );
}

// ── COMPTEUR JOURS ──
export function JoursCompteur({ jours }) {
  const urgence = jours <= 5;
  const critique = jours <= 2;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: critique ? 'var(--error-bg)' : urgence ? 'var(--warning-bg)' : 'var(--light-bl)',
      border: `1px solid ${critique ? 'var(--error)' : urgence ? 'var(--warning)' : 'var(--light-bl2)'}`,
      borderRadius: 'var(--radius-md)', padding: '8px 14px',
      fontSize: '0.85rem',
      color: critique ? 'var(--error)' : urgence ? 'var(--warning)' : 'var(--navy)',
      fontWeight: 500
    }}>
      <span style={{ fontSize: '1rem' }}>{critique ? '🔴' : urgence ? '🟡' : '🟢'}</span>
      <span>{jours} jour{jours > 1 ? 's' : ''} restant{jours > 1 ? 's' : ''}</span>
    </div>
  );
}
